/**
 * Unified event tracker: pushes to GTM dataLayer, fires directly to GA4 via gtag,
 * and mirrors the important conversion events into Meta Pixel via fbq.
 * Safe to call before any loader is ready — all globals are initialized in index.html.
 * Usage: trackEvent("mockup_form_submit", { service: "Fleet" })
 */

// Map our internal event names to Meta Pixel standard events.
// Standard events are what Meta Ads Manager optimizes toward — always prefer these over custom events for conversion campaigns.
const META_EVENT_MAP = {
  mockup_form_submit: "Lead",
  contact_form_submit: "Contact",
  phone_click: "Contact",
};

export function trackEvent(event, params = {}) {
  if (typeof window === "undefined") return;

  // 1) GTM dataLayer — kept so any future GTM tags/triggers still work
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });

  // 2) GA4 direct via gtag — lets you Mark-as-Key-Event in the GA4 UI
  if (typeof window.gtag === "function") {
    window.gtag("event", event, params);
  }

  // 3) Meta Pixel — fire standard events for known conversions, custom for the rest
  if (typeof window.fbq === "function") {
    const metaEvent = META_EVENT_MAP[event];
    if (metaEvent) {
      window.fbq("track", metaEvent, params);
    } else {
      window.fbq("trackCustom", event, params);
    }
  }
}

/**
 * Global click delegator — any element with data-track="event_name" fires that event.
 * Reads data-track-location and data-track-label for context.
 * Called once on app mount.
 */
export function installClickTracker() {
  if (typeof window === "undefined") return;
  document.addEventListener(
    "click",
    (e) => {
      const el = e.target.closest("[data-track]");
      if (!el) return;
      const event = el.getAttribute("data-track");
      const location = el.getAttribute("data-track-location") || undefined;
      const label = el.getAttribute("data-track-label") || undefined;
      trackEvent(event, { location, label });
    },
    { capture: true }
  );
}


/**
 * First-touch traffic attribution (per browser session). Captures UTMs, ad click IDs
 * (gclid = Google Ads auto-tag, fbclid = Meta click), referrer, and landing page so
 * every lead can be traced back to Meta vs Google vs organic/direct.
 */
const ATTR_KEY = "attr_928_v1";

export function captureAttribution() {
  if (typeof window === "undefined") return;
  try {
    if (sessionStorage.getItem(ATTR_KEY)) return; // first touch in the session wins
    const q = new URLSearchParams(window.location.search);
    const attr = {
      utm_source: q.get("utm_source") || "",
      utm_medium: q.get("utm_medium") || "",
      utm_campaign: q.get("utm_campaign") || "",
      gclid: q.get("gclid") ? "yes" : "",
      fbclid: q.get("fbclid") ? "yes" : "",
      referrer: document.referrer ? new URL(document.referrer).hostname : "",
      landing_page: window.location.pathname,
    };
    sessionStorage.setItem(ATTR_KEY, JSON.stringify(attr));
  } catch { /* storage unavailable (private mode) */ }
}

export function getAttribution() {
  try {
    return JSON.parse(sessionStorage.getItem(ATTR_KEY)) || {};
  } catch {
    return {};
  }
}
